"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  useStoreProducts,
  useStoreCategories,
  useDeleteProduct,
  useCreateCategory,
  useUpdateCategory,
  useDeleteCategory,
  type Product,
} from "@/api/store"
import { ProductDialog } from "./product-dialog"
import { PlusIcon, PencilIcon, TrashIcon, TagIcon } from "lucide-react"
import { useToast } from "@/hooks/use-toast"

interface ProductsTabProps {
  openAddDialog?: boolean
  onAddDialogHandled?: () => void
}

export function ProductsTab({ openAddDialog, onAddDialogHandled }: ProductsTabProps) {
  const { toast } = useToast()
  const { data: categories = [] } = useStoreCategories()
  const { data: products = [], isLoading } = useStoreProducts()
  const deleteProduct = useDeleteProduct()
  const createCategory = useCreateCategory()
  const updateCategory = useUpdateCategory()
  const deleteCategory = useDeleteCategory()

  const [search, setSearch] = useState("")
  const [filterCategory, setFilterCategory] = useState<string>("all")

  const [productDialog, setProductDialog] = useState<{
    open: boolean
    product: Product | null
  }>({ open: false, product: null })

  useEffect(() => {
    if (openAddDialog) {
      setProductDialog({ open: true, product: null })
      onAddDialogHandled?.()
    }
  }, [openAddDialog, onAddDialogHandled])

  const [catDialog, setCatDialog] = useState<{
    open: boolean
    id?: string
    name: string
    availabilityMode: string
    availabilityTime: string
  }>({ open: false, name: "", availabilityMode: "", availabilityTime: "" })

  const filtered = products.filter((p) => {
    const matchSearch = p.title.toLowerCase().includes(search.toLowerCase())
    const matchCat =
      filterCategory === "all" ||
      (filterCategory === "uncategorized" ? !p.categoryId : p.categoryId === filterCategory)
    return matchSearch && matchCat
  })

  const handleDeleteProduct = async (id: string) => {
    if (!window.confirm("Delete this product?")) return
    try {
      await deleteProduct.mutateAsync(id)
      toast({ title: "Product deleted" })
    } catch {
      toast({ title: "Failed to delete product", variant: "destructive" })
    }
  }

  const handleSaveCategory = async () => {
    if (!catDialog.name.trim()) return
    const availabilityPayload = {
      availabilityMode: catDialog.availabilityMode || undefined,
      availabilityTime: catDialog.availabilityMode && catDialog.availabilityTime ? catDialog.availabilityTime : undefined,
    }
    try {
      if (catDialog.id) {
        await updateCategory.mutateAsync({ id: catDialog.id, data: { name: catDialog.name, ...availabilityPayload } })
        toast({ title: "Category updated" })
      } else {
        await createCategory.mutateAsync({ name: catDialog.name, ...availabilityPayload })
        toast({ title: "Category created" })
      }
      setCatDialog({ open: false, name: "", availabilityMode: "", availabilityTime: "" })
    } catch {
      toast({ title: "Failed to save category", variant: "destructive" })
    }
  }

  const handleDeleteCategory = async (id: string) => {
    if (!window.confirm("Delete this category? Products won't be deleted.")) return
    try {
      await deleteCategory.mutateAsync(id)
      toast({ title: "Category deleted" })
    } catch {
      toast({ title: "Failed to delete category", variant: "destructive" })
    }
  }

  return (
    <div className="space-y-6">
      {/* Categories management */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <TagIcon className="h-4 w-4" />
            Categories
          </h3>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setCatDialog({ open: true, name: "", availabilityMode: "", availabilityTime: "" })}
          >
            <PlusIcon className="h-3 w-3 mr-1" />
            Add
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {categories.length === 0 && (
            <p className="text-xs text-muted-foreground">No categories yet.</p>
          )}
          {categories.map((cat) => (
            <div key={cat._id} className="flex items-center gap-1 rounded-full border px-3 py-1 text-sm">
              <span>{cat.name}</span>
              {cat.availabilityMode && cat.availabilityTime && (
                <span className="text-xs text-muted-foreground ml-1">
                  ({cat.availabilityMode} {cat.availabilityTime})
                </span>
              )}
              <button
                onClick={() => setCatDialog({ open: true, id: cat._id, name: cat.name, availabilityMode: cat.availabilityMode ?? "", availabilityTime: cat.availabilityTime ?? "" })}
                className="text-muted-foreground hover:text-foreground ml-1"
              >
                <PencilIcon className="h-3 w-3" />
              </button>
              <button
                onClick={() => handleDeleteCategory(cat._id)}
                className="text-muted-foreground hover:text-destructive ml-0.5"
              >
                <TrashIcon className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Products header */}
      <div className="flex flex-col sm:flex-row gap-3">
        <Input
          placeholder="Search products..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="sm:w-56"
        />
        <select
          className="border rounded-md px-3 py-2 text-sm bg-background"
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
        >
          <option value="all">All categories</option>
          <option value="uncategorized">Uncategorized</option>
          {categories.map((c) => (
            <option key={c._id} value={c._id}>{c.name}</option>
          ))}
        </select>
        <Button
          className="ml-auto"
          onClick={() => setProductDialog({ open: true, product: null })}
        >
          <PlusIcon className="h-4 w-4 mr-1" />
          Add Product
        </Button>
      </div>

      {isLoading && (
        <p className="text-center text-muted-foreground py-8">Loading products…</p>
      )}

      {!isLoading && filtered.length === 0 && (
        <p className="text-center text-muted-foreground py-8">No products found.</p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filtered.map((product) => {
          const cat = categories.find((c) => c._id === product.categoryId)
          return (
            <Card key={product._id} className="overflow-hidden">
              <div className="relative aspect-[4/3] overflow-hidden bg-muted">
                {product.mainPhoto ? (
                  <img
                    src={product.mainPhoto}
                    alt={product.title}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full items-center justify-center text-muted-foreground text-xs">
                    No image
                  </div>
                )}
                {!product.available && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <Badge variant="secondary" className="text-xs">Unavailable</Badge>
                  </div>
                )}
              </div>
              <CardContent className="p-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-medium text-sm leading-tight">{product.title}</p>
                  <span className="text-sm font-bold shrink-0">{product.price.toLocaleString()}</span>
                </div>
                {cat && (
                  <Badge variant="outline" className="text-xs">{cat.name}</Badge>
                )}
                {product.description && (
                  <p className="text-xs text-muted-foreground line-clamp-2">{product.description}</p>
                )}
                <div className="flex gap-2 pt-1">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1"
                    onClick={() => setProductDialog({ open: true, product })}
                  >
                    <PencilIcon className="h-3 w-3 mr-1" />
                    Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-destructive hover:text-destructive"
                    onClick={() => handleDeleteProduct(product._id)}
                  >
                    <TrashIcon className="h-3 w-3" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>

      <ProductDialog
        open={productDialog.open}
        onOpenChange={(v) => setProductDialog((prev) => ({ ...prev, open: v }))}
        product={productDialog.product}
        categories={categories}
      />

      {/* Category dialog */}
      <Dialog open={catDialog.open} onOpenChange={(v) => setCatDialog((p) => ({ ...p, open: v }))}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{catDialog.id ? "Edit Category" : "Add Category"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>Name</Label>
              <Input
                value={catDialog.name}
                onChange={(e) => setCatDialog((p) => ({ ...p, name: e.target.value }))}
                placeholder="e.g. Breakfast"
                autoFocus
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Time availability</Label>
                <Select
                  value={catDialog.availabilityMode || "always"}
                  onValueChange={(v) =>
                    setCatDialog((p) => ({
                      ...p,
                      availabilityMode: v === "always" ? "" : v,
                      availabilityTime: v === "always" ? "" : p.availabilityTime,
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="always">Always</SelectItem>
                    <SelectItem value="before">Before</SelectItem>
                    <SelectItem value="after">After</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {catDialog.availabilityMode && (
                <div className="space-y-1">
                  <Label>
                    {catDialog.availabilityMode === "before" ? "Before" : "After"}
                  </Label>
                  <Input
                    type="time"
                    value={catDialog.availabilityTime}
                    onChange={(e) => setCatDialog((p) => ({ ...p, availabilityTime: e.target.value }))}
                  />
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCatDialog({ open: false, name: "", availabilityMode: "", availabilityTime: "" })}>
              Cancel
            </Button>
            <Button onClick={handleSaveCategory}>
              {catDialog.id ? "Save" : "Create"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
